import { useEffect, useReducer, useState } from "react"
import { Accordion, ActionIcon, Alert, Badge, Box, Button, Checkbox, Code, Group, Menu, Modal, ScrollArea, SegmentedControl, Select, Stack, Table, Text, Textarea, TextInput, Tooltip } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { DataTable } from "mantine-datatable"
import { IconAlertTriangle, IconBan, IconCheck, IconDeviceMobile, IconEdit, IconEye, IconGitMerge, IconPackage, IconSearch, IconSend, IconTrash, IconUserPlus, IconX } from "@tabler/icons-react"
import { openConfirmModal } from "@mantine/modals"
import { showNotification } from "@mantine/notifications"
import dayjs from "dayjs"
import { PAGE_SIZES, ROLES } from "@/shared"
import { useAuth } from "@features/auth/hooks/useAuth.ts"
import { useDynamicHeight } from "@/shared/hooks/responsive.queries"
import { headerStore } from "@shared/state/header.state.ts"
import { useLogisticsPermissions } from "../hooks/use-logistics-permissions"
import modalClasses from "@shared/components/Modal.module.scss"
import { useAssignableRiders, useRiders } from "@features/rider/hooks/use-riders"
import { useAssignStops, useCancelDeliveryStop, useDeleteDeliveryStop, useUpdateDeliveryStop, useDeliveryStops, useConfirmDeliveryStop, useConfirmarRecojo, useResendToDiset, useResendToApp, useOdooStatus, useSupabaseStatus, usePreviewDisetPayload, usePreviewAppPayload, useClearNewProductsCount, useExcludeProductsFromItem } from "../hooks/use-delivery-stops"
import { MergeStopsModal } from "../components/MergeStopsModal"
import { deliveryStopInitialState, deliveryStopReducer } from "../state/delivery-stop.state"
import { SCHEDULED_PRESETS, scheduledPresetRange } from "../utils/scheduled-presets"
import { DeliveryStop, DeliveryStopItem, DeliveryStopProducto, ODOO_STATE_COLOR, ODOO_STATE_LABEL, PRODUCTO_TIPO, SaveDeliveryStopDto, STOP_ACTION, STOP_ACTION_LABEL, STOP_STATUS, STOP_STATUS_LABEL } from "../domain/delivery-stop.types"
import { commonCountryOf, esPuntoEcuador, ridersForStops } from "../utils/logistics-country"
import { useCountryScope } from "@/shared/country/use-country-scope"
import { countryQueryParam, filterByCountry } from "@/shared/country/country-scope"
import { CountryBadge, CountryFilterSelect } from "@/shared/country/country-components"

const riderLabel = (rider: DeliveryStop["rider"]): string => {
  if (!rider) return "Sin asignar"
  if (typeof rider === "string") return rider
  return rider.name
}

function productoKey(p: DeliveryStopProducto): string | null {
  if (p.tipo === PRODUCTO_TIPO.SUELTO && p.pickingId != null && p.productId != null) return `suelto:${p.pickingId}:${p.productId}`
  if (p.tipo === PRODUCTO_TIPO.BANDEJA && p.odooBandejaId != null) return `bandeja:${p.odooBandejaId}`
  if (p.tipo === PRODUCTO_TIPO.ACTIVO && p.activoId != null) return `activo:${p.activoId}`
  if (p.tipo === PRODUCTO_TIPO.PAQUETE && p.paqueteId != null) return `paquete:${p.paqueteId}`
  return null
}

const productoLabel = (p: DeliveryStopProducto): string => {
  if (p.tipo === PRODUCTO_TIPO.SUELTO) return p.productCode ? `${p.productCode} — ${p.productName ?? ""}` : (p.productName ?? "Producto suelto")
  if (p.tipo === PRODUCTO_TIPO.BANDEJA) return p.codigoUnico ? `${p.codigoUnico} — ${p.trayName ?? ""}` : (p.trayName ?? "Bandeja")
  if (p.tipo === PRODUCTO_TIPO.ACTIVO) return p.activoCode ? `${p.activoCode} — ${p.activoName ?? ""}` : (p.activoName ?? "Activo")
  if (p.tipo === PRODUCTO_TIPO.PAQUETE) return p.paqueteName ?? "Paquete"
  return "Producto"
}

/** odooState desde los que el motorizado ya salió (entrega) o ya llegó al punto (recojo) — desde ahí no se puede seguir excluyendo productos */
const DELIVERY_DISPATCHED_STATES = ["retiro_de_oficina", "llegada_a_establecimiento", "entregado", "llegada_de_instrumentista", "en_cirugia", "cirugia_finalizada"]
const PICKUP_DISPATCHED_STATES = ["llegada_recojo", "recogido", "retorno_a_oficina", "entrega_en_oficina", "enviado_a_facturar", "procesado"]

const TIPO_ORDER = [PRODUCTO_TIPO.SUELTO, PRODUCTO_TIPO.BANDEJA, PRODUCTO_TIPO.ACTIVO, PRODUCTO_TIPO.PAQUETE]
const tipoBadgeColor: Record<string, string> = { suelto: "indigo", bandeja: "violet", activo: "orange", paquete: "grape" }
const tipoLabel: Record<string, string> = { suelto: "Productos sueltos", bandeja: "Bandejas", activo: "Activos", paquete: "Paquetes" }

const canExcludeProducts = (stop: DeliveryStop | null): boolean => {
  if (!stop) return false
  if (stop.status !== STOP_STATUS.PENDING && stop.status !== STOP_STATUS.CONFIRMED) return false
  if (!stop.odooState) return true
  const dispatched = stop.action === STOP_ACTION.PICKUP ? PICKUP_DISPATCHED_STATES : DELIVERY_DISPATCHED_STATES
  return !dispatched.includes(stop.odooState)
}

export default function DeliveryStopsPage() {
  const { hasRole } = useAuth()
  const { tableHeight } = useDynamicHeight()
  const [state, dispatch] = useReducer(deliveryStopReducer, deliveryStopInitialState)
  // Hoy, mañana o este mes (día de Lima): se calcula en cada render para que "hoy" no quede viejo si la pantalla pasa abierta la medianoche
  const presetRange = state.search.scheduledPreset ? scheduledPresetRange(state.search.scheduledPreset) : null
  // País (EC-15): solo el admin ve la columna y filtra; al resto el back le recorta su país
  const countryScope = useCountryScope()
  const countryFilter = countryQueryParam(countryScope, state.search.country)

  const { data, isFetching, refetch } = useDeliveryStops({
    page: state.page,
    rows: state.rows,
    sortColumn: state.sortStatus.columnAccessor,
    sortDirection: state.sortStatus.direction,
    riderId: state.search.riderId || undefined,
    scheduledDate: state.search.scheduledDate ? dayjs(state.search.scheduledDate).format("YYYY-MM-DD") : undefined,
    scheduledDateFrom: presetRange?.from,
    scheduledDateTo: presetRange?.to,
    action: state.search.action || undefined,
    // El filtro de Estado de la columna manda; si no hay, la vista: "En curso" oculta los
    // completados, "Finalizados" muestra solo los completados y "Todos" no filtra.
    status: state.search.status || (state.search.vista === "finalizados" ? STOP_STATUS.COMPLETED : undefined),
    excludeCompleted: !state.search.status && state.search.vista === "en_curso",
    search: state.search.search || undefined,
    country: countryFilter
  })
  const { data: riders } = useRiders({ rows: 200 })
  const updateStopMutation = useUpdateDeliveryStop()
  const cancelStopMutation = useCancelDeliveryStop()
  const deleteStopMutation = useDeleteDeliveryStop()
  const confirmStopMutation = useConfirmDeliveryStop()
  const confirmarRecojoMutation = useConfirmarRecojo()
  const assignStopsMutation = useAssignStops()
  const resendToDisetMutation = useResendToDiset()
  const resendToAppMutation = useResendToApp()
  const previewDisetMutation = usePreviewDisetPayload()
  const previewAppMutation = usePreviewAppPayload()
  const clearNewProductsMutation = useClearNewProductsCount()
  const excludeProductsMutation = useExcludeProductsFromItem()
  const { data: odooStatus, isFetching: isFetchingOdooStatus } = useOdooStatus()
  const { data: supabaseStatus, isFetching: isFetchingSupabaseStatus } = useSupabaseStatus()
  const permisos = useLogisticsPermissions()

  const [selectedRecords, setSelectedRecords] = useState<DeliveryStop[]>([])

  const [opened, setOpened] = useState(false)
  const [editingStop, setEditingStop] = useState<DeliveryStop | null>(null)
  const [form, setForm] = useState<SaveDeliveryStopDto>({})
  const [itemsModalStop, setItemsModalStop] = useState<DeliveryStop | null>(null)
  const [mergeModalStops, setMergeModalStops] = useState<DeliveryStop[] | null>(null)
  const [excludedTipos, setExcludedTipos] = useState<Set<string>>(new Set())
  const [excludedMotivos, setExcludedMotivos] = useState<Record<string, string>>({})
  useEffect(() => {
    headerStore.setTitle("Entrega y Recojo de Productos")
    headerStore.setOnRefresh(() => refetch())
    return () => {
      headerStore.setTitle(null)
      headerStore.setOnRefresh(null)
    }
  }, [refetch])

  const handleViewItems = (stop: DeliveryStop) => {
    setItemsModalStop(stop)
    if ((stop.newProductsCount ?? 0) > 0) clearNewProductsMutation.mutate(stop.id)
  }

  const openEditModal = (stop: DeliveryStop) => {
    setEditingStop(stop)
    setForm({
      rider: typeof stop.rider === "string" ? stop.rider : stop.rider?.id,
      notes: stop.notes ?? ""
    })
    setExcludedTipos(new Set())
    setExcludedMotivos({})
    setOpened(true)
  }

  const handleSave = async () => {
    if (!form.rider || !editingStop) return

    if (excludedTipos.size > 0) {
      const faltaMotivo = [...excludedTipos].some((tipo) => !excludedMotivos[tipo]?.trim())
      if (faltaMotivo) {
        showNotification({
          title: "Falta el motivo",
          message: "Indique el motivo por el que no se llevará cada categoría desmarcada antes de guardar.",
          color: "red"
        })
        return
      }

      const remainingItems = editingStop.items?.map((item) => ({ ...item })) ?? []
      for (const item of editingStop.items ?? []) {
        if (item.soId == null) continue
        for (const tipo of excludedTipos) {
          const productosAExcluir = (item.productos ?? []).filter((p) => p.tipo === tipo)
          if (!productosAExcluir.length) continue

          await excludeProductsMutation.mutateAsync({ id: editingStop.id, soId: item.soId, productos: productosAExcluir, motivo: excludedMotivos[tipo] })

          const target = remainingItems.find((i) => i.soId === item.soId)
          if (target) target.productos = (target.productos ?? []).filter((p) => p.tipo !== tipo)
        }
      }
      setEditingStop({ ...editingStop, items: remainingItems })
    }

    await updateStopMutation.mutateAsync({ id: editingStop.id, stopData: { rider: form.rider, notes: form.notes } })
    setOpened(false)
    setEditingStop(null)
    setExcludedTipos(new Set())
    setExcludedMotivos({})
  }

  /** Los productos sueltos nunca se pueden excluir (en Perú, por ser indispensables para DISET/RFID; Ecuador no tiene DISET y la regla es la misma) */
  const toggleTipo = (tipo: string, checked: boolean) => {
    if (tipo === PRODUCTO_TIPO.SUELTO) return
    setExcludedTipos((prev) => {
      const next = new Set(prev)
      if (checked) next.delete(tipo)
      else next.add(tipo)
      return next
    })
    if (checked) {
      setExcludedMotivos((prev) => {
        const { [tipo]: _removed, ...rest } = prev
        return rest
      })
    }
  }

  const handleConfirm = async (stop: DeliveryStop) => {
    const isPickup = stop.action === STOP_ACTION.PICKUP

    const disetPreview = await previewDisetMutation.mutateAsync(stop.id).catch(() => null)
    // Ecuador no tiene DISET ni RFID: el diálogo habla solo de Odoo y de la app
    const sinDiset = esPuntoEcuador(stop)
    const appPreview = isPickup ? null : await previewAppMutation.mutateAsync(stop.id).catch(() => null)
    if (!disetPreview || (!isPickup && !appPreview)) {
      showNotification({
        title: "No se pudo previsualizar la confirmación",
        message: "Revise la notificación de error anterior para más detalle (ej. el motorizado asignado no tiene un email válido en Supabase). No se abrió el modal de confirmación.",
        color: "red"
      })
      return
    }

    const isSuperuser = hasRole(ROLES.ADMIN)

    openConfirmModal({
      title: isPickup ? "Confirmar punto de recojo" : "Confirmar punto de entrega",
      centered: true,
      size: "lg",
      children: (
        <Stack gap="xs">
          <Text size="sm">
            {isPickup
              ? `¿Confirmar este punto de recojo? Se enviará lo siguiente a Odoo${sinDiset ? "" : " (quien lo reenvía a DISET)"}:`
              : `¿Confirmar este punto de entrega? Se enviará lo siguiente a Odoo${sinDiset ? "" : " (quien lo reenvía a DISET)"} y a la app del motorizado:`}
          </Text>
          {isSuperuser && (
            <Accordion defaultValue="odoo" variant="separated">
              <Accordion.Item value="odoo">
                <Accordion.Control>{sinDiset ? "Payload a Odoo (solo productos sueltos)" : "Payload a Odoo / DISET (solo productos sueltos)"}</Accordion.Control>
                <Accordion.Panel>
                  <ScrollArea.Autosize mah={280}>
                    <Code block>{JSON.stringify(disetPreview.data, null, 2)}</Code>
                  </ScrollArea.Autosize>
                </Accordion.Panel>
              </Accordion.Item>
              {!isPickup && (
                <Accordion.Item value="app">
                  <Accordion.Control>Payload a la app móvil (todos los grupos de producto)</Accordion.Control>
                  <Accordion.Panel>
                    <ScrollArea.Autosize mah={280}>
                      <Code block>{JSON.stringify(appPreview?.data, null, 2)}</Code>
                    </ScrollArea.Autosize>
                  </Accordion.Panel>
                </Accordion.Item>
              )}
            </Accordion>
          )}
        </Stack>
      ),
      labels: { confirm: "Confirmar", cancel: "Volver" },
      confirmProps: { color: "teal", loading: isPickup ? confirmarRecojoMutation.isPending : confirmStopMutation.isPending, leftSection: <IconCheck size={15} /> },
      onConfirm: async () => {
        if (isPickup) {
          await confirmarRecojoMutation.mutateAsync(stop.id)
        } else {
          await confirmStopMutation.mutateAsync(stop.id)
        }
      }
    })
  }

  const handleCancel = (stop: DeliveryStop) => {
    openConfirmModal({
      title: "Cancelar punto de entrega",
      centered: true,
      children: <Text size="sm">¿Cancelar este punto de entrega? Se guardará en el historial como cancelado y no podrá volver a pendiente.</Text>,
      labels: { confirm: "Cancelar punto", cancel: "Volver" },
      confirmProps: { color: "orange", loading: cancelStopMutation.isPending, leftSection: <IconBan size={15} /> },
      onConfirm: async () => {
        await cancelStopMutation.mutateAsync(stop.id)
      }
    })
  }

  const handleDelete = (stop: DeliveryStop) => {
    openConfirmModal({
      title: "Eliminar punto de entrega",
      centered: true,
      children: <Text size="sm">¿Está seguro de eliminar este punto de entrega? Esta acción no se puede deshacer.</Text>,
      labels: { confirm: "Eliminar", cancel: "Cancelar" },
      confirmProps: { color: "red", loading: deleteStopMutation.isPending, leftSection: <IconTrash size={15} /> },
      onConfirm: async () => {
        await deleteStopMutation.mutateAsync(stop.id)
      }
    })
  }

  const handleResendToDiset = async (stop: DeliveryStop) => {
    const isSuperuser = hasRole(ROLES.ADMIN)
    // Mismo preview que "Confirmar" -- pedido explícito: el superuser debe ver qué se va a reenviar,
    // igual que ahí. No bloquea el reenvío si el preview falla (a diferencia de confirmar, acá no es
    // un requisito para poder reenviar, solo informativo).
    const disetPreview = isSuperuser ? await previewDisetMutation.mutateAsync(stop.id).catch(() => null) : null
    const sinDiset = esPuntoEcuador(stop)

    openConfirmModal({
      title: "Reenviar a Odoo",
      centered: true,
      size: isSuperuser ? "lg" : undefined,
      children: (
        <Stack gap="xs">
          <Text size="sm">¿Está seguro de mandar la actualización del punto {stop.correlativo ?? ""} a Odoo{sinDiset ? "" : " (DISET)"}?</Text>
          {isSuperuser && disetPreview && (
            <ScrollArea.Autosize mah={280}>
              <Code block>{JSON.stringify(disetPreview.data, null, 2)}</Code>
            </ScrollArea.Autosize>
          )}
        </Stack>
      ),
      labels: { confirm: "Reenviar", cancel: "Cancelar" },
      confirmProps: { color: "teal", loading: resendToDisetMutation.isPending, leftSection: <IconSend size={15} /> },
      onConfirm: async () => {
        await resendToDisetMutation.mutateAsync(stop.id)
      }
    })
  }

  const handleResendToApp = async (stop: DeliveryStop) => {
    const isSuperuser = hasRole(ROLES.ADMIN)
    const appPreview = isSuperuser ? await previewAppMutation.mutateAsync(stop.id).catch(() => null) : null

    openConfirmModal({
      title: "Reenviar a la app móvil",
      centered: true,
      size: isSuperuser ? "lg" : undefined,
      children: (
        <Stack gap="xs">
          <Text size="sm">¿Está seguro de mandar la actualización del punto {stop.correlativo ?? ""} a la app móvil?</Text>
          {isSuperuser && appPreview && (
            <ScrollArea.Autosize mah={280}>
              <Code block>{JSON.stringify(appPreview.data, null, 2)}</Code>
            </ScrollArea.Autosize>
          )}
        </Stack>
      ),
      labels: { confirm: "Reenviar", cancel: "Cancelar" },
      confirmProps: { color: "grape", loading: resendToAppMutation.isPending, leftSection: <IconSend size={15} /> },
      onConfirm: async () => {
        await resendToAppMutation.mutateAsync(stop.id)
      }
    })
  }

  const handleBulkDelete = () => {
    openConfirmModal({
      title: "Eliminar puntos de entrega seleccionados",
      centered: true,
      children: <Text size="sm">¿Eliminar {selectedRecords.length} punto{selectedRecords.length !== 1 ? "s" : ""} de entrega? Esta acción no se puede deshacer.</Text>,
      labels: { confirm: "Eliminar", cancel: "Cancelar" },
      confirmProps: { color: "red", leftSection: <IconTrash size={15} /> },
      onConfirm: async () => {
        await Promise.all(selectedRecords.map((stop) => deleteStopMutation.mutateAsync(stop.id)))
        setSelectedRecords([])
      }
    })
  }

  // Selectores de asignación: solo asignables (sin "Libre" ni inactivos). El filtro de la columna usa todos, para poder ver el historial.
  const activeRiders = useAssignableRiders()
  // Solo se ofrecen despachadores del país de los puntos (el back omite los de otro país); para Perú son los de siempre
  const bulkRiders = ridersForStops(activeRiders, selectedRecords)
  const selectedMixCountries = selectedRecords.length > 0 && !commonCountryOf(selectedRecords)
  const editRiders = editingStop ? ridersForStops(activeRiders, [editingStop]) : activeRiders

  const itemsModalProductos: DeliveryStopProducto[] = (itemsModalStop?.items ?? []).flatMap((item) => item.productos ?? [])
  // Los pickings de paquete (segmento de almacén "LIMAP", ej. "LIMAP/LIMAP/Clinica/00022") llegan de
  // Odoo dentro de productos_sueltos pero son el contenido de un paquete: el detalle solo lista los
  // productos sueltos de verdad (el paquete ya se ve en su propia sección).
  const esPickingDePaquete = (pickingName?: string | null) => !!pickingName && /(^|\/)limap(\/|$)/i.test(pickingName)
  const itemsModalSueltos = itemsModalProductos.filter((p) => p.tipo === PRODUCTO_TIPO.SUELTO && !esPickingDePaquete(p.pickingName))
  const itemsModalBandejas = itemsModalProductos.filter((p) => p.tipo === PRODUCTO_TIPO.BANDEJA)
  const itemsModalActivos = itemsModalProductos.filter((p) => p.tipo === PRODUCTO_TIPO.ACTIVO)
  const itemsModalPaquetes = itemsModalProductos.filter((p) => p.tipo === PRODUCTO_TIPO.PAQUETE)
  const totalSueltosUnidades = itemsModalSueltos.reduce(
    (acc, p) => acc + (p.lots?.reduce((lotAcc, lot) => lotAcc + (lot.qty ?? 0), 0) ?? 0),
    0
  )
  const totalPaquetes = itemsModalPaquetes.length

  const canOfferMerge = selectedRecords.length >= 2 && permisos.stopEdit && selectedRecords.every((s) => s.status === STOP_STATUS.PENDING)
  const selectedDestinations = new Set(selectedRecords.map((s) => s.address ?? ""))
  const hasMixedDestinations = canOfferMerge && selectedDestinations.size > 1

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group>
          <SegmentedControl
            value={state.search.vista}
            onChange={(value) => dispatch({ type: "SET_SEARCH", payload: { vista: value as "en_curso" | "finalizados" | "todos" } })}
            data={[
              { value: "en_curso", label: "En curso" },
              { value: "finalizados", label: "Finalizados" },
              { value: "todos", label: "Todos" }
            ]}
          />
          {canOfferMerge && (
            <Tooltip label="No se puede fusionar: los puntos seleccionados tienen destinos (direcciones) diferentes" disabled={!hasMixedDestinations}>
              <Button
                color={hasMixedDestinations ? "gray" : "grape"}
                variant="light"
                leftSection={hasMixedDestinations ? <IconAlertTriangle size={18} /> : <IconGitMerge size={18} />}
                disabled={hasMixedDestinations}
                onClick={() => setMergeModalStops(selectedRecords)}
              >
                {hasMixedDestinations ? "Destinos diferentes" : `Mezclar puntos (${selectedRecords.length})`}
              </Button>
            </Tooltip>
          )}
          {selectedRecords.length > 0 && permisos.stopAssign && (
            <Menu withinPortal shadow="md">
              <Menu.Target>
                <Button variant="light" leftSection={<IconUserPlus size={18} />} loading={assignStopsMutation.isPending}>
                  Asignar despachador ({selectedRecords.length})
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{selectedMixCountries ? "Elige puntos de un solo país para asignarlos" : "Asignar a (no cambia el estado del recojo)"}</Menu.Label>
                {bulkRiders.map((rider) => (
                  <Menu.Item
                    key={rider.id}
                    onClick={async () => {
                      await assignStopsMutation.mutateAsync({ stopIds: selectedRecords.map((stop) => stop.id), riderId: rider.id }).catch(() => undefined)
                      setSelectedRecords([])
                    }}
                  >
                    {rider.name}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          )}
          {selectedRecords.length > 0 && permisos.stopDelete && (
            <Button color="red" variant="light" leftSection={<IconTrash size={18} />} onClick={handleBulkDelete}>
              Eliminar seleccionados ({selectedRecords.length})
            </Button>
          )}
        </Group>
        <Group gap="xs">
          <Tooltip
            label={
              odooStatus
                ? `${odooStatus.connected ? "Conectado" : "Sin respuesta"} — ${odooStatus.database || "sin BD configurada"} (${odooStatus.url || "sin URL configurada"})`
                : "Consultando estado de Odoo..."
            }
          >
            <Badge
              variant="dot"
              color={isFetchingOdooStatus ? "gray" : odooStatus?.connected ? "green" : "red"}
              size="lg"
            >
              Odoo 19: {odooStatus?.database?.includes("test") ? "Pruebas (19-test)" : odooStatus?.database || "—"}
            </Badge>
          </Tooltip>
          <Tooltip
            label={
              supabaseStatus
                ? `${supabaseStatus.connected ? "Conectado" : "Sin respuesta"} (${supabaseStatus.url || "sin URL configurada"}) — app móvil`
                : "Consultando estado de Supabase..."
            }
          >
            <Badge
              variant="dot"
              color={isFetchingSupabaseStatus ? "gray" : supabaseStatus?.connected ? "green" : "red"}
              size="lg"
            >
              App móvil
            </Badge>
          </Tooltip>
        </Group>
      </Group>

      <DataTable
        withTableBorder
        borderRadius="md"
        striped
        highlightOnHover
        fetching={isFetching}
        records={data.data}
        selectedRecords={selectedRecords}
        onSelectedRecordsChange={setSelectedRecords}
        totalRecords={data.totalRecords || 0}
        recordsPerPage={state.rows}
        page={state.page}
        onPageChange={(page) => dispatch({ type: "SET_PAGE", payload: page })}
        recordsPerPageOptions={PAGE_SIZES}
        onRecordsPerPageChange={(rows) => dispatch({ type: "SET_ROWS", payload: rows })}
        recordsPerPageLabel="Cantidad por página"
        sortStatus={state.sortStatus}
        onSortStatusChange={(sortStatus) => dispatch({ type: "SET_SORT", payload: sortStatus })}
        pinLastColumn
        rowStyle={(record) => {
          if (record.status === STOP_STATUS.CANCELLED) return { textDecoration: "line-through", opacity: 0.6 }
          if (record.items?.some((item) => item.isEmergency)) return { backgroundColor: "var(--mantine-color-red-0)" }
          return undefined
        }}
        columns={[
          {
            accessor: "correlativo",
            title: "Correlativo",
            width: 160,
            noWrap: true,
            render: (record) => {
              const isEmergency = record.items?.some((item) => item.isEmergency)
              return record.correlativo ? (
                <Group gap={6} wrap="nowrap">
                  <Text fz={13} fw={600} c="indigo">
                    {record.correlativo}
                  </Text>
                  {isEmergency && (
                    <Tooltip label="Cirugía de emergencia — prioridad en la ruta">
                      <Badge color="red" variant="filled" size="sm" leftSection={<IconAlertTriangle size={11} />}>
                        Urgente
                      </Badge>
                    </Tooltip>
                  )}
                  {(record.newProductsCount ?? 0) > 0 && (
                    <Tooltip label={`${record.newProductsCount} producto(s) nuevo(s) se agregaron automáticamente — revisar ítems`}>
                      <Badge color="yellow" variant="filled" size="sm" style={{ cursor: "pointer" }} onClick={() => handleViewItems(record)}>
                        +{record.newProductsCount} nuevo{record.newProductsCount === 1 ? "" : "s"}
                      </Badge>
                    </Tooltip>
                  )}
                </Group>
              ) : (
                <Text fz={13} c="dimmed">
                  —
                </Text>
              )
            }
          },
          {
            accessor: "country",
            title: "País",
            width: 110,
            hidden: !countryScope.canSeeAllCountries,
            filtering: !!state.search.country,
            render: (record) => <CountryBadge country={record.country} />,
            filter: <CountryFilterSelect value={state.search.country} onChange={(country) => dispatch({ type: "SET_SEARCH", payload: { country } })} />
          },
          {
            accessor: "items",
            title: "Ítems",
            width: 90,
            render: (record) => {
              const productoCount = (record.items ?? []).reduce((acc, item) => acc + (item.productos?.length ?? 0), 0)
              if (!productoCount) return <Text fz={13} c="dimmed">—</Text>
              return (
                <Badge
                  variant="light"
                  color="indigo"
                  style={{ cursor: "pointer" }}
                  leftSection={<IconPackage size={12} />}
                  onClick={() => handleViewItems(record)}
                >
                  {productoCount}
                </Badge>
              )
            }
          },
          {
            accessor: "scheduledDate",
            title: "Fecha y hora programada",
            width: 210,
            noWrap: true,
            sortable: true,
            filtering: state.search.scheduledDate !== null || state.search.scheduledPreset !== null,
            render: (record) => (record.scheduledDate ? dayjs(record.scheduledDate).format("DD/MM/YYYY HH:mm") : "-"),
            filter: (
              <Stack gap="xs">
                <Group gap="xs" wrap="nowrap">
                  {SCHEDULED_PRESETS.map((preset) => {
                    const active = state.search.scheduledPreset === preset.value
                    return (
                      <Button
                        key={preset.value}
                        size="compact-sm"
                        variant={active ? "filled" : "light"}
                        onClick={() => dispatch({ type: "SET_SEARCH", payload: { scheduledPreset: active ? null : preset.value, scheduledDate: null } })}
                      >
                        {preset.label}
                      </Button>
                    )
                  })}
                </Group>
                <DatePickerInput
                  popoverProps={{ withinPortal: false }}
                  valueFormat="DD/MM/YYYY"
                  placeholder="Buscar por fecha"
                  clearable
                  value={state.search.scheduledDate}
                  onChange={(value) => dispatch({ type: "SET_SEARCH", payload: { scheduledDate: value as Date | null, scheduledPreset: null } })}
                />
              </Stack>
            )
          },
          {
            accessor: "surgeryDate",
            title: "Fecha y hora de cirugía",
            width: 210,
            noWrap: true,
            render: (record) => {
              const surgeryDate = record.items?.[0]?.surgeryDate
              return surgeryDate ? dayjs(surgeryDate).format("DD/MM/YYYY HH:mm") : "-"
            }
          },
          {
            accessor: "rider",
            title: "Repartidor",
            width: 180,
            noWrap: true,
            filtering: state.search.riderId !== "",
            render: (record) => riderLabel(record.rider),
            filter: (
              <Select
                comboboxProps={{ withinPortal: false }}
                clearable
                searchable
                placeholder="Buscar por repartidor"
                value={state.search.riderId}
                onChange={(value) => dispatch({ type: "SET_SEARCH", payload: { riderId: value || "" } })}
                data={filterByCountry(riders.data || [], countryFilter).map((rider) => ({ value: rider.id, label: rider.name }))}
              />
            )
          },
          {
            accessor: "action",
            title: "Acción",
            sortable: true,
            filtering: state.search.action !== "",
            render: (record) => <Badge variant="light">{record.action ? STOP_ACTION_LABEL[record.action] : "-"}</Badge>,
            filter: (
              <Select
                comboboxProps={{ withinPortal: false }}
                clearable
                placeholder="Buscar por acción"
                value={state.search.action}
                onChange={(value) => dispatch({ type: "SET_SEARCH", payload: { action: value || "" } })}
                data={Object.values(STOP_ACTION).map((action) => ({ value: action, label: STOP_ACTION_LABEL[action] }))}
              />
            )
          },
          {
            accessor: "destino",
            title: "Destino",
            width: 220,
            // Recojo de Odoo = inversa de la entrega: sale de la clínica y termina en la oficina.
            render: (record) =>
              record.officeAddress ? (
                <Stack gap={0}>
                  <Text fz={13}>{record.officeAddress}</Text>
                  <Text fz={11} c="dimmed">
                    Origen: {record.address || "-"}
                  </Text>
                </Stack>
              ) : (
                record.address || "-"
              )
          },
          {
            accessor: "patientName",
            title: "Paciente",
            width: 200,
            render: (record) => record.items?.map((i) => i.patientName).filter(Boolean).join(", ") || "-"
          },
          {
            accessor: "so",
            title: "SO",
            width: 220,
            // Pedido explícito del usuario (23/09): un punto puede agrupar varias SO (misma clínica/día)
            // -- antes se veían como un solo string largo separado por comas, ahora cada SO es su propio
            // badge para distinguirlas de un vistazo cuando hay 2 o más.
            render: (record) => {
              const soNames = [...new Set(record.items?.map((i) => i.soName).filter(Boolean))]
              if (soNames.length === 0) return "-"
              return (
                <Group gap={4} wrap="wrap">
                  {soNames.map((so) => (
                    <Badge key={so} color="gray" variant="light" size="sm">
                      {so}
                    </Badge>
                  ))}
                </Group>
              )
            },
            filtering: state.search.search !== "",
            filter: (
              <TextInput
                placeholder="Buscar por clínica, paciente, SO o médico"
                leftSection={<IconSearch size={14} />}
                rightSection={
                  state.search.search && (
                    <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={() => dispatch({ type: "SET_SEARCH", payload: { search: "" } })}>
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={state.search.search}
                onChange={(e) => dispatch({ type: "SET_SEARCH", payload: { search: e.currentTarget.value } })}
              />
            )
          },
          {
            accessor: "odooState",
            title: "Estado Odoo",
            width: 200,
            render: (record) =>
              record.odooState ? (
                <Badge color={ODOO_STATE_COLOR[record.odooState] ?? "gray"} variant="light" size="sm">
                  {ODOO_STATE_LABEL[record.odooState] ?? record.odooState}
                </Badge>
              ) : (
                "-"
              )
          },
          {
            accessor: "doctorName",
            title: "Médico",
            width: 200,
            render: (record) => record.items?.map((i) => i.doctorName).filter(Boolean).join(", ") || "-"
          },
          {
            accessor: "status",
            title: "Estado",
            width: 150,
            sortable: true,
            filtering: state.search.status !== "",
            render: (record) => {
              const color =
                record.status === STOP_STATUS.COMPLETED
                  ? "green"
                  : record.status === STOP_STATUS.CANCELLED
                    ? "gray"
                    : record.status === STOP_STATUS.IN_TRANSIT
                      ? "blue"
                      : record.status === STOP_STATUS.CONFIRMED
                        ? "teal"
                        : "yellow"
              return <Badge color={color} variant="light">{record.status ? STOP_STATUS_LABEL[record.status] : "-"}</Badge>
            },
            filter: (
              <Select
                comboboxProps={{ withinPortal: false }}
                clearable
                placeholder="Buscar por estado"
                value={state.search.status}
                onChange={(value) => dispatch({ type: "SET_SEARCH", payload: { status: value || "" } })}
                data={Object.values(STOP_STATUS).map((status) => ({ value: status, label: STOP_STATUS_LABEL[status] }))}
              />
            )
          },
          { accessor: "suggestedOrder", title: "Orden sugerido", sortable: true, render: (record) => record.suggestedOrder ?? "-" },
          { accessor: "legDistanceMeters", title: "Distancia tramo", render: (record) => (record.legDistanceMeters ? `${(record.legDistanceMeters / 1000).toFixed(1)} km` : "-") },
          { accessor: "legDurationSeconds", title: "Tiempo tramo", render: (record) => (record.legDurationSeconds ? `${Math.round(record.legDurationSeconds / 60)} min` : "-") },
          {
            accessor: "actions",
            title: "Acciones",
            textAlign: "right",
            hidden: !permisos.anyStopAction,
            render: (stop) => {
              const isPickup = stop.action === STOP_ACTION.PICKUP
              return (
              <Group gap={4} justify="right" wrap="nowrap">
                <Tooltip label={isPickup ? "Ver detalle del punto de recojo" : "Ver detalle del punto de entrega"}>
                  <ActionIcon size="sm" variant="subtle" color="gray.7" onClick={() => handleViewItems(stop)}>
                    <IconEye size={16} />
                  </ActionIcon>
                </Tooltip>
                {permisos.stopEdit && (
<Tooltip label={isPickup ? "Editar punto de recojo" : "Editar punto de entrega"}>
                  <ActionIcon size="sm" variant="subtle" color="blue.7" onClick={() => openEditModal(stop)}>
                    <IconEdit size={16} />
                  </ActionIcon>
                </Tooltip>
)}
                {stop.status === STOP_STATUS.PENDING && (
                  <>
                    {permisos.stopConfirm && (
<Tooltip label={isPickup ? "Confirmar punto de recojo" : "Confirmar punto de entrega"}>
                      <ActionIcon size="sm" variant="subtle" color="teal.7" loading={previewDisetMutation.isPending || previewAppMutation.isPending} onClick={() => handleConfirm(stop)}>
                        <IconCheck size={16} />
                      </ActionIcon>
                    </Tooltip>
)}
                    {permisos.stopCancel && (
<Tooltip label={isPickup ? "Cancelar punto de recojo" : "Cancelar punto de entrega"}>
                      <ActionIcon size="sm" variant="subtle" color="orange.7" onClick={() => handleCancel(stop)}>
                        <IconBan size={16} />
                      </ActionIcon>
                    </Tooltip>
)}
                  </>
                )}
                {(stop.status === STOP_STATUS.CONFIRMED || stop.status === STOP_STATUS.IN_TRANSIT) && (
                  <>
                    {permisos.stopConfirm && (
<Tooltip label={esPuntoEcuador(stop) ? "Reenviar a Odoo (por si falló el envío)" : "Reenviar a Odoo (por si falló DISET)"}>
                      <ActionIcon size="sm" variant="subtle" color="teal.7" loading={resendToDisetMutation.isPending} onClick={() => handleResendToDiset(stop)}>
                        <IconSend size={16} />
                      </ActionIcon>
                    </Tooltip>
)}
                    {!isPickup && permisos.stopConfirm && (
                      <Tooltip label="Reenviar a la app móvil (por si falló Supabase)">
                        <ActionIcon size="sm" variant="subtle" color="grape.7" loading={resendToAppMutation.isPending} onClick={() => handleResendToApp(stop)}>
                          <IconDeviceMobile size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </>
                )}
                {permisos.stopDelete && (
<Tooltip label="Quitar de la vista">
                  <ActionIcon size="sm" variant="subtle" color="red.7" onClick={() => handleDelete(stop)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
)}
              </Group>
              )
            }
          }
        ]}
        style={{ height: tableHeight }}
        noRecordsText="No se encontraron puntos de entrega"
      />

      <Modal.Root opened={opened} onClose={() => setOpened(false)} centered size="xl">
        <Modal.Overlay />
        <Modal.Content radius="md">
          <Modal.Header className={modalClasses.modalHeader}>
            <Modal.Title className={modalClasses.modalTitle}>Editar Punto de entrega</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body className={modalClasses.modalBody}>
        <Stack>
          <Group grow align="flex-end">
            <Select
              label="Repartidor"
              placeholder="Seleccione un repartidor"
              searchable
              value={form.rider}
              onChange={(value) => setForm({ ...form, rider: value || "" })}
              data={editRiders.map((rider) => ({ value: rider.id, label: rider.name }))}
              required
            />
            <Stack gap={4}>
              <Text size="sm" fw={500}>
                Acción
              </Text>
              <Badge size="lg" variant="light" color={editingStop?.action === STOP_ACTION.PICKUP ? "orange" : "teal"}>
                {editingStop?.action ? STOP_ACTION_LABEL[editingStop.action] : "-"}
              </Badge>
            </Stack>
          </Group>

          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Fecha y hora programada
            </Text>
            <Badge size="lg" variant="light" color="gray">
              {editingStop?.scheduledDate ? dayjs(editingStop.scheduledDate).format("DD/MM/YYYY HH:mm") : "-"}
            </Badge>
          </Stack>

          {editingStop?.officeAddress && (
            <Stack gap={4}>
              <Text size="sm" fw={500}>
                Origen (clínica)
              </Text>
              <Text size="sm" c="dimmed">
                {editingStop.address ?? "-"}
              </Text>
            </Stack>
          )}

          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Destino{editingStop?.officeAddress ? " (oficina)" : ""}
            </Text>
            <Text size="sm" c="dimmed">
              {editingStop?.officeAddress ?? editingStop?.address ?? "-"}
            </Text>
          </Stack>

          <Textarea label="Notas" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} autosize minRows={2} />

          {editingStop && (editingStop.items ?? []).some((i) => (i.productos?.length ?? 0) > 0) && (
            <Stack gap={6}>
              <Text size="sm" fw={500}>
                Productos en este punto
              </Text>
              <Text size="xs" c="dimmed">
                Desmarque una categoría completa para NO llevarla en este punto — al guardar quedará en la bolsa de productos pendientes de asignar. Indique el motivo en cada categoría desmarcada.{esPuntoEcuador(editingStop) ? " Los productos sueltos no se pueden excluir." : " Los productos sueltos no se pueden excluir: son indispensables para el envío a DISET (RFID)."}
              </Text>
              {!canExcludeProducts(editingStop) && (
                <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
                  Este punto ya está en tránsito (odooState="{editingStop.odooState}") — no se pueden excluir categorías.
                </Alert>
              )}
              <ScrollArea.Autosize mah={420}>
                <Accordion multiple defaultValue={[]} variant="separated">
                  {TIPO_ORDER.map((tipo) => {
                    const rows = (editingStop.items ?? [])
                      .flatMap((item) => (item.productos ?? []).filter((p) => p.tipo === tipo).map((p) => ({ item, producto: p, key: productoKey(p) })))
                      .filter((r): r is { item: DeliveryStopItem; producto: DeliveryStopProducto; key: string } => !!r.key)
                    if (!rows.length) return null
                    const isSuelto = tipo === PRODUCTO_TIPO.SUELTO
                    const included = isSuelto || !excludedTipos.has(tipo)
                    return (
                      <Accordion.Item key={tipo} value={tipo}>
                        <Accordion.Control>
                          <Stack gap={6}>
                            <Group gap={8} wrap="nowrap">
                              <span onClick={(e) => e.stopPropagation()}>
                                <Tooltip label={esPuntoEcuador(editingStop) ? "Los productos sueltos no se pueden excluir" : "Indispensable para DISET (RFID) — no se puede excluir"} disabled={!isSuelto}>
                                  <Checkbox
                                    checked={included}
                                    disabled={isSuelto || !canExcludeProducts(editingStop)}
                                    onChange={(e) => toggleTipo(tipo, e.currentTarget.checked)}
                                  />
                                </Tooltip>
                              </span>
                              <Badge size="sm" color={tipoBadgeColor[tipo]}>
                                {tipoLabel[tipo]}
                              </Badge>
                              <Text size="sm">
                                {rows.length} producto(s){isSuelto ? " · obligatorio" : !included ? " · se excluirá de este punto" : ""}
                              </Text>
                            </Group>
                            {!included && (
                              <Box onClick={(e) => e.stopPropagation()}>
                                <TextInput
                                  size="xs"
                                  placeholder="Motivo por el que no se llevará esta categoría en este punto"
                                  value={excludedMotivos[tipo] ?? ""}
                                  onChange={(e) => setExcludedMotivos((prev) => ({ ...prev, [tipo]: e.currentTarget.value }))}
                                  required
                                />
                              </Box>
                            )}
                          </Stack>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap={2}>
                            {rows.map(({ item, producto, key }) => (
                              <Text key={key} size="sm" c={included ? undefined : "dimmed"} td={included ? undefined : "line-through"}>
                                {productoLabel(producto)}
                                {item.soName ? ` · ${item.soName}` : ""}
                              </Text>
                            ))}
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    )
                  })}
                </Accordion>
              </ScrollArea.Autosize>
            </Stack>
          )}
        </Stack>
          </Modal.Body>

          <Box className={modalClasses.fixedButtons}>
            <Group grow wrap="nowrap">
              <Button onClick={handleSave} loading={updateStopMutation.isPending || excludeProductsMutation.isPending}>
                Guardar punto de entrega
              </Button>
            </Group>
          </Box>
        </Modal.Content>
      </Modal.Root>

      <Modal
        opened={!!itemsModalStop}
        onClose={() => setItemsModalStop(null)}
        title={`Detalle del punto ${itemsModalStop?.correlativo ?? ""}`}
        centered
        size="xl"
      >
        <Stack gap="md">
          <Stack gap={6}>
            <Group gap={8} wrap="wrap">
              {itemsModalStop?.status && (
                <Badge
                  color={
                    itemsModalStop.status === STOP_STATUS.CANCELLED
                      ? "red"
                      : itemsModalStop.status === STOP_STATUS.IN_TRANSIT
                        ? "blue"
                        : itemsModalStop.status === STOP_STATUS.CONFIRMED
                          ? "teal"
                          : "gray"
                  }
                >
                  {STOP_STATUS_LABEL[itemsModalStop.status]}
                </Badge>
              )}
              <Badge variant="light" color={itemsModalStop?.action === STOP_ACTION.PICKUP ? "orange" : "teal"}>
                {itemsModalStop?.action ? STOP_ACTION_LABEL[itemsModalStop.action] : "-"}
              </Badge>
              <Badge variant="light" color="gray">
                {itemsModalStop?.scheduledDate ? dayjs(itemsModalStop.scheduledDate).format("DD/MM/YYYY HH:mm") : "-"}
              </Badge>
            </Group>
            <Text size="sm">
              <Text span fw={500}>
                Repartidor:{" "}
              </Text>
              {itemsModalStop ? riderLabel(itemsModalStop.rider) : "-"}
            </Text>
            {itemsModalStop?.officeAddress && (
              <Text size="sm">
                <Text span fw={500}>
                  Origen (clínica):{" "}
                </Text>
                {itemsModalStop.address ?? "-"}
              </Text>
            )}
            <Text size="sm">
              <Text span fw={500}>
                Destino{itemsModalStop?.officeAddress ? " (oficina)" : ""}:{" "}
              </Text>
              {itemsModalStop?.officeAddress ?? itemsModalStop?.address ?? "-"}
            </Text>
            {itemsModalStop?.notes && (
              <Text size="sm">
                <Text span fw={500}>
                  Notas:{" "}
                </Text>
                {itemsModalStop.notes}
              </Text>
            )}
            {itemsModalProductos.length > 0 && (
              <Text size="sm">
                <Text span fw={500}>
                  Total sueltos:{" "}
                </Text>
                {totalSueltosUnidades} unidad(es)
                <Text span fw={500} ml="md">
                  Total paquetes:{" "}
                </Text>
                {totalPaquetes}
              </Text>
            )}
            {itemsModalStop?.action === STOP_ACTION.DELIVERY && itemsModalStop?.ventanaEntrega && (
              <Text size="xs" c="dimmed">
                Ventana de entrega{itemsModalStop.ventanaEntrega.zonaReparto ? ` (${itemsModalStop.ventanaEntrega.zonaReparto})` : ""}: recibir antes de las{" "}
                {itemsModalStop.ventanaEntrega.horaLimiteRecepcionImplante ?? "—"}
              </Text>
            )}
            {itemsModalStop?.action === STOP_ACTION.PICKUP && itemsModalStop?.ventanaRecojo && (
              <Text size="xs" c="dimmed">
                Ventana de recojo: {itemsModalStop.ventanaRecojo.horaRecojoInstrumental ?? "—"}
                {itemsModalStop.ventanaRecojo.diasHabilitados?.length ? ` · días habilitados: ${itemsModalStop.ventanaRecojo.diasHabilitados.join(", ")}` : ""}
              </Text>
            )}
          </Stack>

          {itemsModalProductos.length === 0 ? (
            <Text c="dimmed" size="sm">
              Sin productos.
            </Text>
          ) : (
            <ScrollArea.Autosize mah={420}>
              <Accordion multiple defaultValue={[]} variant="separated">
                {itemsModalSueltos.length > 0 && (
                  <Accordion.Item value="sueltos">
                    <Accordion.Control>
                      <Badge size="sm" color="indigo" mr="xs">
                        Sueltos
                      </Badge>
                      {itemsModalSueltos.length} producto(s)
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Table fz={12} striped withTableBorder>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Producto</Table.Th>
                            <Table.Th>Picking</Table.Th>
                            <Table.Th style={{ textAlign: "right" }}>Cant.</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {itemsModalSueltos.map((p, i) => {
                            const qty = p.lots?.reduce((acc, lot) => acc + (lot.qty ?? 0), 0)
                            return (
                              <Table.Tr key={i}>
                                <Table.Td>{p.productCode ? `${p.productCode} — ${p.productName ?? ""}` : (p.productName ?? "—")}</Table.Td>
                                <Table.Td>{p.pickingName ?? "—"}</Table.Td>
                                <Table.Td style={{ textAlign: "right" }}>{qty ?? "—"}</Table.Td>
                              </Table.Tr>
                            )
                          })}
                        </Table.Tbody>
                      </Table>
                    </Accordion.Panel>
                  </Accordion.Item>
                )}

                {itemsModalBandejas.length > 0 && (
                  <Accordion.Item value="bandejas">
                    <Accordion.Control>
                      <Badge size="sm" color="violet" mr="xs">
                        Bandejas
                      </Badge>
                      {itemsModalBandejas.length} bandeja(s)
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Table fz={12} striped withTableBorder>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Bandeja</Table.Th>
                            <Table.Th style={{ textAlign: "right" }}>Activos dentro</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {itemsModalBandejas.map((b, i) => (
                            <Table.Tr key={i}>
                              <Table.Td>{b.codigoUnico ? `${b.codigoUnico} — ${b.trayName ?? ""}` : (b.trayName ?? "Bandeja")}</Table.Td>
                              <Table.Td style={{ textAlign: "right" }}>{b.activos?.length ?? 0}</Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Accordion.Panel>
                  </Accordion.Item>
                )}

                {itemsModalActivos.length > 0 && (
                  <Accordion.Item value="activos">
                    <Accordion.Control>
                      <Badge size="sm" color="orange" mr="xs">
                        Activos
                      </Badge>
                      {itemsModalActivos.length} activo(s)
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Table fz={12} striped withTableBorder>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Activo</Table.Th>
                            <Table.Th>Producto vinculado</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {itemsModalActivos.map((a, i) => (
                            <Table.Tr key={i}>
                              <Table.Td>{a.activoCode ? `${a.activoCode} — ${a.activoName ?? ""}` : (a.activoName ?? "—")}</Table.Td>
                              <Table.Td>{a.productName ?? "—"}</Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Accordion.Panel>
                  </Accordion.Item>
                )}

                {itemsModalPaquetes.length > 0 && (
                  <Accordion.Item value="paquetes">
                    <Accordion.Control>
                      <Badge size="sm" color="grape" mr="xs">
                        Paquetes
                      </Badge>
                      {itemsModalPaquetes.length} paquete(s)
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Table fz={12} striped withTableBorder>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Paquete</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {itemsModalPaquetes.map((p, i) => (
                            <Table.Tr key={i}>
                              <Table.Td>{p.paqueteName ?? "—"}</Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Accordion.Panel>
                  </Accordion.Item>
                )}
              </Accordion>
            </ScrollArea.Autosize>
          )}
        </Stack>
      </Modal>

      <MergeStopsModal
        stops={mergeModalStops ?? []}
        opened={!!mergeModalStops}
        onClose={() => {
          setMergeModalStops(null)
          setSelectedRecords([])
        }}
        onSaved={(survivors) => {
          setMergeModalStops(null)
          setSelectedRecords(survivors)
        }}
      />
    </Stack>
  )
}
